import React, { useEffect, useRef } from "react";
import { Row } from "ignite-components";
import { TableRow } from "Table/interfaces";
import { Td, Tr, Th } from "Table/style";
import { connect } from "react-redux";

import Store, { injectReducer, setState, StoreState } from "Store";

import { hoverIndexes, tableDims, fixedTableDims } from "./reducers";
import { SET_HOVER, SET_DIMS, SET_FIXED_DIMS, SetHoverAction, SetDimsAction } from "./actionTypes";

interface OwnProps {
    row: TableRow;
    fixed?: boolean;
    rowIndex: number;
    height?: number;
    columnWidths?: number[];
    header?: boolean;
}

const mapStateToProps = (state: StoreState, ownProps: OwnProps) => ({
    shouldHover: state.hoverIndexes.row === ownProps.rowIndex,
});

const mapDispatchToProps = (dispatch: (dispatch: SetHoverAction | SetDimsAction) => void) => ({
    onHover: (row: number, col: number) => {
        dispatch(setState(SET_HOVER, { data: { row, col } }));
    },
    onMount: (rowIndex: number, fixed: boolean | undefined, el: HTMLTableRowElement, width: Array<number>) => {
        if (fixed) {
            dispatch(setState(SET_FIXED_DIMS, { data: { rowIndex, el, width } }));
        } else {
            dispatch(setState(SET_DIMS, { data: { rowIndex, el, width } }));
        }
    },
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const RowElement: React.FC<Props> = ({
    row,
    shouldHover,
    rowIndex,
    onMount,
    fixed,
    onHover,
    height,
    header,
    columnWidths,
}) => {
    const el = useRef<HTMLTableRowElement>(null);
    const elementWidths: number[] = [];

    useEffect(() => {
        el.current && onMount(rowIndex, fixed, el.current, elementWidths);
    }, [el]);

    const onMouseOver = (event: React.SyntheticEvent<HTMLTableCellElement>) => {
        const i = Array(...(event.target as any).parentElement.children).indexOf(event.target);
        onHover(rowIndex, i);
    };

    const Cell = header ? Th : Td;

    return (
        <Tr ref={el} style={{ width: "100%" }}>
            {row.cells.map((cell, i) => (
                <Cell
                    ref={(el: HTMLTableCellElement | null) => {
                        if (rowIndex === 0 && el) {
                            elementWidths[i] = el.clientWidth + (i !== 0 ? 1 : 0);
                        }
                    }}
                    width={columnWidths && columnWidths[i]}
                    height={height}
                    fixed={fixed}
                    hover={shouldHover}
                    onMouseOver={onMouseOver}
                    key={i}
                    progress={cell.progress}
                    progressColor={cell.progressColor}
                    value={cell.value}
                    percent={cell.percent}
                >
                    <Row align={cell.align || "start"}>{cell.progress ? "" : cell.value}</Row>
                </Cell>
            ))}
        </Tr>
    );
};

// Inject the async reducer to the store
injectReducer(Store, { key: "hoverIndexes", reducer: hoverIndexes });
injectReducer(Store, { key: "tableDims", reducer: tableDims });
injectReducer(Store, { key: "fixedTableDims", reducer: fixedTableDims });

const MemoizedRowElement = React.memo(connect(mapStateToProps, mapDispatchToProps)(RowElement));

export default MemoizedRowElement;
